// src/admin/packAnnotations.js
//
// Pure helpers for pack-scoped terminology (annotations). The NO-CLOBBER guarantee
// lives here and is unit-tested: annotations live in the SAME /config/terminology
// array as global glossary terms and other packs' annotations, so every edit is a
// full-array read-modify-write that must touch ONLY this pack's entries and leave
// every other entry byte-identical (same object reference). Pure + dependency-free
// so the functions test runner can import it.
//
// Term shape: { id, name, category, definition, example, tip, scope?, packIds? }.
// A pack annotation carries scope:'pack' + packIds including this packId. Global
// terms omit scope (implicitly global) and are NEVER matched/mutated here.

const isThisPackAnn = (t, packId) =>
    !!t && t.scope === 'pack' && Array.isArray(t.packIds) && t.packIds.includes(packId);

// This pack's annotations only (global + other packs' terms excluded).
export function selectPackAnnotations(terms, packId) {
    return (Array.isArray(terms) ? terms : []).filter((t) => isThisPackAnn(t, packId));
}

// A fresh annotation id unique across the WHOLE terms array (so it can never collide
// with a global term or another pack's annotation).
export function makeAnnotationId(terms, packId) {
    const all = Array.isArray(terms) ? terms : [];
    const base = 'ann-' + packId;
    let i = 1;
    let id = base + '-' + i;
    while (all.some((t) => t && t.id === id)) { i += 1; id = base + '-' + i; }
    return id;
}

// Create-or-edit by id, returning a NEW full array. Non-matching entries are passed
// through by reference (untouched). The entry is normalised to scope:'pack' + a
// packIds that includes this packId (preserving any existing multi-pack membership).
export function upsertPackAnnotation(terms, packId, ann) {
    const all = Array.isArray(terms) ? terms : [];
    const packIds = (Array.isArray(ann.packIds) && ann.packIds.includes(packId))
        ? ann.packIds
        : [packId];
    const entry = { ...ann, scope: 'pack', packIds };
    const exists = all.some((t) => t && t.id === entry.id);
    return exists
        ? all.map((t) => (t && t.id === entry.id ? entry : t))
        : [...all, entry];
}

// Remove one entry by id, returning a NEW full array; all others pass through by ref.
export function removeAnnotation(terms, id) {
    return (Array.isArray(terms) ? terms : []).filter((t) => !(t && t.id === id));
}

// Bulk merge-by-id for the global Terminology "Import (merge)" action. Mirrors
// upsertPackAnnotation's no-clobber guarantee over a WHOLE incoming array, with the
// Felix-approved semantics:
//   - id COLLISION (incoming.id === an existing term's id) -> UPDATE (last-wins):
//       the incoming term replaces the existing one in place.
//   - NO MATCH -> append the incoming term.
//   - every non-matching existing term is passed through UNCHANGED (by reference).
//   - id-less incoming term -> REJECT THE WHOLE IMPORT: throws an Error listing each
//       offending incoming term (1-based index + name) and merges NOTHING, so the
//       caller's live draft is left exactly as-is (atomic).
//   - the output never contains duplicate ids (later incoming entries with the same id
//       win over earlier ones, consistent with last-wins).
// Pure: returns a NEW array; neither input is mutated. Unlike upsertPackAnnotation this
// does NOT stamp scope/packIds -- a bulk import carries whatever scope each term already
// has (global terms stay global; pack annotations keep their scope:'pack' + packIds).
export function mergeTermsById(existing, incoming) {
    const base = Array.isArray(existing) ? existing : [];
    const incomingArr = Array.isArray(incoming) ? incoming : [];

    // Atomic precheck: any id-less incoming term rejects the entire import.
    const missing = [];
    incomingArr.forEach((t, i) => {
        const id = t && t.id;
        if (id === undefined || id === null || id === '') {
            missing.push('#' + (i + 1) + ' "' + ((t && t.name) || '(unnamed)') + '"');
        }
    });
    if (missing.length > 0) {
        throw new Error(
            'Import rejected: ' + missing.length + ' incoming term'
            + (missing.length === 1 ? '' : 's') + ' missing an "id" -> '
            + missing.join(', ') + '. Add an id to every term and re-import; nothing was merged.'
        );
    }

    // Start from the existing array (preserve order + references for untouched terms),
    // then upsert each incoming term by id: replace in place on collision, else append.
    const result = base.slice();
    const indexById = new Map();
    result.forEach((t, i) => { if (t && t.id !== undefined && t.id !== null) indexById.set(t.id, i); });

    for (const term of incomingArr) {
        const at = indexById.get(term.id);
        if (at === undefined) {
            indexById.set(term.id, result.length);
            result.push(term);
        } else {
            result[at] = term; // last-wins: incoming replaces existing (or an earlier incoming)
        }
    }
    return result;
}

// Union two ordered category lists: keep `existing` order, then append any `incoming`
// category not already present. Returns a NEW array; inputs unmutated. Mirrors the
// single-add pattern in TerminologyTermsEditor.saveEdit / PackAnnotationsTab.save.
export function mergeCategories(existing, incoming) {
    const base = Array.isArray(existing) ? existing : [];
    const add = Array.isArray(incoming) ? incoming : [];
    const seen = new Set(base);
    const result = base.slice();
    for (const c of add) {
        if (c !== undefined && c !== null && c !== '' && !seen.has(c)) {
            seen.add(c);
            result.push(c);
        }
    }
    return result;
}
